import type { LoaderFunctionArgs } from "react-router-dom";
import { fetchAccount, fetchAccounts, fetchDashboard, fetchMe, fetchMilestone, fetchMilestones, fetchRequest, fetchRequests, fetchTags, fetchTeam } from "./api";
import type { RequestListOptions } from "./api";
import type { Account, Dashboard, MilestoneDetail, MilestoneSummary, RequestDetail, RequestSummary, Tag, TeamMember } from "./types";

export interface RequestsData {
  requests: RequestSummary[];
  accounts: Account[];
}

/** The request table's filters, read from the page URL. Shared by the
 *  Requests page and the account page, which pins `account` itself. */
function tableQuery(url: string): RequestListOptions {
  const p = new URL(url).searchParams;
  const priorityRaw = p.get("priority");
  return {
    owner: p.get("owner") ?? undefined,
    lead: p.get("lead") ?? undefined,
    account: p.get("account") ?? undefined,
    // The table shows every status unless narrowed; "open" is the API's own default set.
    status: p.get("status") ?? "all",
    priority: priorityRaw !== null && priorityRaw !== "" ? Number(priorityRaw) : undefined,
    tag: p.get("tag") ?? undefined,
    createdFrom: p.get("created_from") ?? undefined,
    createdTo: p.get("created_to") ?? undefined,
    updatedFrom: p.get("updated_from") ?? undefined,
    updatedTo: p.get("updated_to") ?? undefined,
    neededFrom: p.get("needed_from") ?? undefined,
    neededTo: p.get("needed_to") ?? undefined,
    sort: p.get("sort") ?? undefined,
  };
}

export async function requestsLoader({ request }: LoaderFunctionArgs): Promise<RequestsData> {
  // Accounts are fetched too, to populate the Account filter dropdown.
  const [requests, accounts] = await Promise.all([fetchRequests(tableQuery(request.url)), fetchAccounts()]);
  return { requests, accounts };
}

export interface RequestData {
  request: RequestDetail;
  /** Every milestone on this request's account, for the picker. Fetched after
   *  the request because only it knows the account id — RequestSummary carries
   *  the account NAME. */
  milestones: MilestoneSummary[];
  /** For the Account select: a request filed against the wrong customer has to
   *  be movable to any of them. */
  accounts: Account[];
}

export async function requestLoader({ params }: LoaderFunctionArgs): Promise<RequestData> {
  const request = await fetchRequest(params.id ?? "");
  const [milestones, accounts] = await Promise.all([fetchMilestones({ account: request.accountId, status: "all" }), fetchAccounts()]);
  return { request, milestones, accounts };
}

export interface CapacityData {
  /** Every open request, in the server's canonical order — the server's
   *  default set is exactly what CAPACITY_CLASS does not mark closed. */
  requests: RequestSummary[];
}

export async function capacityLoader(): Promise<CapacityData> {
  return { requests: await fetchRequests() };
}

export interface MilestonesData {
  milestones: MilestoneSummary[];
  accounts: Account[];
}

export async function milestonesLoader({ request: req }: LoaderFunctionArgs): Promise<MilestonesData> {
  const p = new URL(req.url).searchParams;
  const [milestones, accounts] = await Promise.all([
    fetchMilestones({ account: p.get("account") ?? undefined, status: p.get("status") ?? "all" }),
    fetchAccounts(),
  ]);
  return { milestones, accounts };
}

export async function milestoneLoader({ params }: LoaderFunctionArgs): Promise<MilestoneDetail> {
  return fetchMilestone(params.id ?? "");
}

export interface MyWorkData {
  /** Null when the signed-in person is not linked to a team member yet. */
  memberId: string | null;
  requests: RequestSummary[];
}

export async function myWorkLoader(): Promise<MyWorkData> {
  const me = await fetchMe();
  if (!me.memberId) return { memberId: null, requests: [] };
  // Every status where I am the technical owner or the account lead, split
  // into open / recently closed in the component.
  return { memberId: me.memberId, requests: await fetchRequests({ member: me.memberId, status: "all" }) };
}

export interface DashboardData {
  dashboard: Dashboard;
  /** For the New-request dialog's account list. */
  accounts: Account[];
}

export async function dashboardLoader(): Promise<DashboardData> {
  const [dashboard, accounts] = await Promise.all([fetchDashboard(), fetchAccounts()]);
  return { dashboard, accounts };
}

export async function accountsLoader(): Promise<Account[]> {
  return fetchAccounts();
}

export interface AccountDetailData {
  account: Account;
  /** The account's requests, through the same filters as the Requests page. */
  requests: RequestSummary[];
}

export async function accountLoader({ params, request }: LoaderFunctionArgs): Promise<AccountDetailData> {
  const id = params.id ?? "";
  // The account is the page's, whatever ?account= says.
  const [account, requests] = await Promise.all([fetchAccount(id), fetchRequests({ ...tableQuery(request.url), account: id })]);
  return { account, requests };
}

export async function teamLoader(): Promise<TeamMember[]> {
  // Settings is the one place that shows who has left.
  return fetchTeam({ includeLeft: true });
}

export async function tagsLoader(): Promise<Tag[]> {
  return fetchTags();
}
